import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ActionCell, DataSourceValuesList, EntityFieldsList } from './columns';
import { getDatasourceFields } from '../../../../../../api/forms';
import { getUITA } from '../../../../../../utils/commonUtils';
import { PREDEFINED_DATA_SOURCES } from '../../../../../constants/form-manager.constants';
import { getEntityFields } from '../../../../../utils/editor.utils';

const getPredefinedDatasourceFields = (columnList) =>
  columnList.map((value) => ({ Name: value, Label: value }));

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableBody-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="EmptyRow-middleAlign"]': {
      height: '90px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
    '& [class*="MuiFormControl-marginNormal"]': {
      marginTop: '0px',
    },
  },
});

const columns = [
  {
    header: 'To',
    accessor: 'DummyTo',
    customCell: EntityFieldsList,
  },
  {
    header: 'From',
    accessor: 'DummyFrom',
    customCell: DataSourceValuesList,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const MappingModal = ({
  rows,
  setRows,
  selectedTarget,
  selectedDataSource,
  entityDefinition,
  translations: { Entity },
}) => {
  const classes = useStyles();
  const [dataSourceFields, setDataSourceFields] = useState([]);

  const entityFields = useMemo(() => getEntityFields(entityDefinition, Entity, selectedTarget), [
    entityDefinition,
    Entity,
    selectedTarget,
  ]);

  const { dataSourceTypeStr, dataSourceName, dataSourceKey, columnList } = selectedDataSource ?? {};

  useEffect(() => {
    const getFieldsList = async () => {
      if (PREDEFINED_DATA_SOURCES.includes(dataSourceKey)) {
        setDataSourceFields(getPredefinedDatasourceFields(columnList));
      } else {
        const { hasError, fields } = await getDatasourceFields({
          entityKey: dataSourceName,
          dataLookupType: dataSourceTypeStr,
        });

        if (!hasError && fields) {
          setDataSourceFields(fields);
        }
      }
    };

    getFieldsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteRow = (id) => setRows((prevRows) => prevRows.filter((row) => row.id !== id));

  const editRow = (id, newRow) =>
    setRows((prevRows) => prevRows.map((row) => (row.id === id ? newRow : row)));

  const addRow = () =>
    setRows((prevRows) => [
      ...prevRows,
      {
        id: uuidv4(),
        DummyFrom: '',
        From: '',
        DummyTo: '',
        To: '',
        ToDataType: '',
      },
    ]);

  const CustomHeader = () => (
    <div>
      <Button
        variant="text"
        size="small"
        onClick={addRow}
        ui-ta={getUITA('Update Actions Mapping Table', 'Add Btn')}
      >
        {'Add Mapping'}
      </Button>
    </div>
  );

  return (
    <div className={classes.root} ui-ta={getUITA('Update Actions Mapping Table')}>
      <Table
        title="Mapping List"
        columns={columns}
        rows={rows.map((row) => ({
          ...row,
          key: row.id,
          deleteRow,
          editRow,
          dataSourceFields,
          entityFields,
        }))}
        initialSortOrder="asc"
        rowProps={{ 'ui-ta': getUITA('Update Actions Mapping Table', 'Row') }}
        hidePagination
        CustomHeader={CustomHeader}
      />
    </div>
  );
};

export default MappingModal;
